import { Box, IconButton, Link, Typography } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { MdHistory, MdOutlineContentCopy, MdOutlineEdit } from 'react-icons/md';
import { RiInboxArchiveLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import * as urls from '@/routes/config';

import DataTable from '@/components/Common/DataTable';
import Pagination from '@/components/Common/Pagination';
import Tooltip from '@/components/Common/Tooltip';
import OnboardingTicketIcon from '@/assets/icons/OnboardingTicketIcon';
import BusinessIcon from '@/assets/icons/BusinessIcon';
import BusinessListIntegrationIcon from '@/assets/icons/BusinessListIntegrationIcon';
import { normalizeWebsiteUrl } from '@/utils/helperFunctions';
import { getStageTimeColor, OnboardingStatus, OnboardingTableRow, SOURCE_CONFIG, STATUS_CONFIG } from '../constants';

interface SelectedFormState {
  id: number;
  businessName: string;
  status: OnboardingStatus;
  businessPlanId?: string | number | null;
  voiceEngineId?: number | null;
  overallProgress?: number;
}

interface OnboardingListingProps {
  data: OnboardingTableRow[];
  isLoading: boolean;
  totalPages: number;
  totalCount: number;
  currentPage: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onOpenSetup: (form: SelectedFormState) => void;
  onCopyLink: (data: { businessName: string; contactPerson: string; onboardingUrl: string }) => void;
  onArchive: (id: string) => void;
}

export default function OnboardingListing({
  data,
  isLoading,
  totalPages,
  totalCount,
  currentPage,
  perPage,
  onPageChange,
  onPerPageChange,
  onOpenSetup,
  onCopyLink,
  onArchive
}: OnboardingListingProps) {
  const navigate = useNavigate();

  return (
    <>
      <DataTable
        data={data}
        isLoading={isLoading}
        columns={getColumns(navigate, onOpenSetup, onCopyLink, onArchive)}
        isHeaderSticky
      />
      <Pagination
        totalPages={totalPages}
        totalCount={totalCount}
        currentPage={currentPage}
        perPage={perPage}
        onChange={onPageChange}
        onPerPageChange={onPerPageChange}
      />
    </>
  );
}

function getColumns(
  navigate: ReturnType<typeof useNavigate>,
  onOpenSetup: (form: SelectedFormState) => void,
  onCopyLink: (data: { businessName: string; contactPerson: string; onboardingUrl: string }) => void,
  onArchive: (id: string) => void
): ColumnDef<OnboardingTableRow>[] {
  return [
    {
      header: 'Business Name',
      accessorKey: 'business_name',
      cell: (info) => {
        const businessName = (info.getValue() as string) || 'N/A';
        const integrations = info.row.original.connected_integrations;
        return (
          <Box display='flex' alignItems='center' gap={0.5}>
            <Tooltip title={businessName} placement='top'>
              <Typography
                sx={{
                  display: 'block',
                  maxWidth: 120,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {businessName}
              </Typography>
            </Tooltip>
            {integrations.length > 0 && (
              <Tooltip
                title={
                  <Box display='flex' flexDirection='column' gap={0.5}>
                    {integrations.map((integration) => (
                      <span key={integration.id}>{integration.name}</span>
                    ))}
                  </Box>
                }
                placement='top'
              >
                <Box display='flex' alignItems='center' sx={{ cursor: 'pointer' }}>
                  <BusinessListIntegrationIcon size={18} />
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    {
      header: 'Primary Contact',
      accessorKey: 'primary_contact'
    },
    {
      header: 'Website',
      accessorKey: 'website',
      cell: (info) => {
        const website = (info.getValue() as string) || '';
        if (!website) return 'N/A';

        const href = normalizeWebsiteUrl(website);
        return (
          <Tooltip title={website} placement='top'>
            <Link
              href={href}
              target='_blank'
              rel='noopener noreferrer'
              underline='hover'
              sx={{
                display: 'block',
                maxWidth: 160,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {website}
            </Link>
          </Tooltip>
        );
      }
    },
    {
      header: 'Source',
      accessorKey: 'source',
      cell: (info) => {
        const source = (info.getValue() as string)?.toLowerCase();
        const config = SOURCE_CONFIG[source];
        if (!config) return null;

        return (
          <Typography
            noWrap
            sx={{
              color: config.color,
              backgroundColor: config.bg,
              borderRadius: '20px',
              px: 2,
              py: 0.5,
              fontSize: 12,
              textAlign: 'center',
              width: 'fit-content',
              fontWeight: 400
            }}
          >
            {config.label}
          </Typography>
        );
      }
    },
    {
      header: 'Created On',
      accessorKey: 'created_on',
      cell: (info) => <Typography noWrap>{info.row.original.created_on}</Typography>
    },
    {
      header: 'Updated On',
      accessorKey: 'updated_at',
      cell: (info) => <Typography noWrap>{info.row.original.updated_at}</Typography>
    },
    {
      header: 'Status',
      cell: (info) => {
        const { status, days_in_stage: daysInStage } = info.row.original;
        const config = STATUS_CONFIG[status];
        return (
          <Box display='flex' flexDirection='column' gap={0.5}>
            <Typography
              noWrap
              sx={{
                color: config?.color,
                backgroundColor: config?.bg,
                borderRadius: '20px',
                px: 2,
                py: 0.5,
                fontSize: 12,
                width: 'fit-content'
              }}
            >
              {config?.label}
            </Typography>
            {config?.label && config?.label !== 'Completed' && daysInStage !== null && (
              <Typography noWrap sx={{ fontSize: 11, color: getStageTimeColor(daysInStage), pl: 1 }}>
                &bull; {daysInStage} {daysInStage === 1 ? 'Day' : 'Days'} in Stage
              </Typography>
            )}
          </Box>
        );
      }
    },
    {
      header: 'Action',
      cell: (info) => {
        const formId = info.row.original.id;
        const formToken = info.row.original.form_token;
        const ticketRef = info.row.original.ticket_reference;
        const isArchived = Boolean(info.row.original.discarded_at);

        return (
          <Box display='flex' gap={1}>
            <Tooltip title='View history logs' placement='top'>
              <IconButton size='small' onClick={() => navigate(`history/${formId}`)}>
                <MdHistory size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Copy form link for client' placement='top'>
              <IconButton
                size='small'
                onClick={() =>
                  onCopyLink({
                    businessName: info.row.original.business_name,
                    contactPerson: info.row.original.primary_contact,
                    onboardingUrl: formToken || ''
                  })
                }
              >
                <MdOutlineContentCopy size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title={ticketRef ? 'Open ticket reference' : 'Ticket reference not available'} placement='top'>
              <IconButton
                size='small'
                disabled={!ticketRef}
                onClick={() => window.open(ticketRef, '_blank', 'noopener,noreferrer')}
              >
                <OnboardingTicketIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title='Business Setup' placement='top'>
              <IconButton
                size='small'
                onClick={() =>
                  onOpenSetup({
                    id: formId,
                    businessName: info.row.original.business_name,
                    status: info.row.original.status,
                    businessPlanId: info.row.original.business_plan_id,
                    voiceEngineId: info.row.original.voice_engine_id,
                    overallProgress: info.row.original.overall_progress
                  })
                }
              >
                <BusinessIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title='Edit' placement='top'>
              <IconButton
                size='small'
                onClick={() => navigate(`/agency/${urls.agency.onboardingFormEdit(formId.toString())}`)}
              >
                <MdOutlineEdit size={20} />
              </IconButton>
            </Tooltip>
            {!isArchived && (
              <Tooltip title='Archive' placement='top'>
                <IconButton size='small' onClick={() => onArchive(String(formId))} sx={{ color: '#777777' }}>
                  <RiInboxArchiveLine size={20} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    }
  ];
}
